// Round-by-round visualization for a completed (or in-progress) game.
// Renders inside GameDetail in place of the bare frame list.
//
// What's shown:
//   1. Cumulative score line chart — every game (live or virtual)
//      has scoringTeam + netPoints per frame, so we can compute the
//      cumulative scores at the end of each round and plot two lines
//      with a target threshold.
//   2. Per-round net momentum bars — signed bars where +N = team1
//      scored N net points that round, -N = team2 scored. Reads the
//      swings + comebacks at a glance.
//   3. Bag accuracy breakdown — only for games that recorded
//      per-bag in_hole / on_board counts. Cornhole is 4 bags per team
//      per round; an in-hole bag is 3pts, on-board is 1pt, miss is 0.
//      Summary across the game + per-round bag dots.
//   4. Frame log table — the old flat list, kept below the charts so
//      you can still read each round's specifics.
//
// All charts are inline SVG (no chart lib) to keep the bundle lean
// and match the existing Sparkline style on the Virtual page.

import type { Game, GameFrame } from "@/lib/types";

type Props = {
  game: Game;
};

export function GameRoundsReport({ game }: Props) {
  const frames = (game.frames ?? []).slice().sort(
    (a, b) => a.roundNumber - b.roundNumber,
  );
  if (frames.length === 0) {
    return (
      <div className="rounded-md border border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
        No rounds recorded yet.
      </div>
    );
  }

  // Walk frames in order, accumulating cumulative scores. Each frame
  // already encodes the NET (after cancellation) so we just bump the
  // scoringTeam's running total.
  const cum = computeCumulative(frames);
  const team1Label = game.team1Players.map((p) => p.name).join(" & ") || "Team 1";
  const team2Label = game.team2Players.map((p) => p.name).join(" & ") || "Team 2";

  // Detect whether any frame carries detailed bag counts. If yes,
  // we render the accuracy section; if no, we skip it (virtual games
  // and hand-tapped live games only have netPoints).
  const anyBagDetail = frames.some(
    (f) =>
      f.team1InHole != null ||
      f.team1OnBoard != null ||
      f.team2InHole != null ||
      f.team2OnBoard != null,
  );

  return (
    <div className="space-y-4">
      <section>
        <ReportSectionHeader
          title="Score progression"
          subtitle={`Cumulative score across ${frames.length} round${frames.length === 1 ? "" : "s"}. Target: ${game.targetScore}.`}
        />
        <CumulativeScoreChart
          rounds={cum}
          target={game.targetScore}
          team1Label={team1Label}
          team2Label={team2Label}
          winningTeam={game.winningTeam}
        />
      </section>

      <section>
        <ReportSectionHeader
          title="Round momentum"
          subtitle="Signed net points per round. Above the axis = team 1 took the round; below = team 2."
        />
        <RoundMomentumChart
          rounds={cum}
          team1Label={team1Label}
          team2Label={team2Label}
        />
      </section>

      {anyBagDetail ? (
        <section>
          <ReportSectionHeader
            title="Bag accuracy"
            subtitle="Per-team bag distribution. Cornhole has 4 bags per team per round; in-hole = 3 pts, on-board = 1 pt."
          />
          <BagAccuracyPanel
            frames={frames}
            team1Label={team1Label}
            team2Label={team2Label}
          />
        </section>
      ) : null}

      <section>
        <ReportSectionHeader
          title="Frame log"
          subtitle="Each round in order. Use the round number to cross-reference the charts above."
        />
        <FrameLogTable
          frames={frames}
          cum={cum}
          team1Label={team1Label}
          team2Label={team2Label}
        />
      </section>
    </div>
  );
}

// -------------------- helpers + sub-components --------------------

function ReportSectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="mb-2">
      <div className="text-xs font-display uppercase tracking-widest">
        {title}
      </div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
    </div>
  );
}

type CumRow = {
  round: number;
  team1: number;
  team2: number;
  net1: number; // signed: positive = team1 scored that round
  scoringTeam: 1 | 2 | null;
};

function computeCumulative(frames: GameFrame[]): CumRow[] {
  let t1 = 0;
  let t2 = 0;
  const rows: CumRow[] = [];
  for (const f of frames) {
    let net = 0;
    if (f.scoringTeam === 1) {
      t1 += f.netPoints;
      net = f.netPoints;
    } else if (f.scoringTeam === 2) {
      t2 += f.netPoints;
      net = -f.netPoints;
    }
    rows.push({
      round: f.roundNumber,
      team1: t1,
      team2: t2,
      net1: net,
      scoringTeam: f.scoringTeam,
    });
  }
  return rows;
}

// Cumulative score line chart with two team lines and a target
// threshold. The target line gives a sense of "how close were we"
// at every point in the game.
function CumulativeScoreChart({
  rounds,
  target,
  team1Label,
  team2Label,
  winningTeam,
}: {
  rounds: CumRow[];
  target: number;
  team1Label: string;
  team2Label: string;
  winningTeam: 1 | 2 | null;
}) {
  const W = 600;
  const H = 200;
  const PAD = { top: 12, right: 12, bottom: 20, left: 28 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  // X domain: 0 (start, both 0) → last round number. We synthesize an
  // implicit (round=0, team1=0, team2=0) point so the lines start at
  // the origin instead of jumping in at round 1.
  const lastRound = rounds[rounds.length - 1]!.round;
  const xMax = Math.max(1, lastRound);
  const maxScore = Math.max(
    target,
    rounds[rounds.length - 1]!.team1,
    rounds[rounds.length - 1]!.team2,
  );
  // Headroom above max so the highest point isn't on the top edge.
  const yMax = Math.max(target + 2, maxScore + 2);

  const x = (r: number) => PAD.left + (r / xMax) * innerW;
  const y = (s: number) => PAD.top + innerH - (s / yMax) * innerH;

  // Build polyline point strings starting at (0,0).
  const t1Points = ["0,0", ...rounds.map((r) => `${r.round},${r.team1}`)]
    .map((p) => {
      const [rRaw, sRaw] = p.split(",");
      return `${x(Number(rRaw)).toFixed(1)},${y(Number(sRaw)).toFixed(1)}`;
    })
    .join(" ");
  const t2Points = ["0,0", ...rounds.map((r) => `${r.round},${r.team2}`)]
    .map((p) => {
      const [rRaw, sRaw] = p.split(",");
      return `${x(Number(rRaw)).toFixed(1)},${y(Number(sRaw)).toFixed(1)}`;
    })
    .join(" ");

  // Y-axis gridlines: target + multiples of 7 (3, 7, 14, 21 read
  // naturally for cornhole). Keep at most 4 lines so the chart
  // doesn't get noisy.
  const gridYs = Array.from(
    new Set([0, Math.round(target / 2), target]),
  ).filter((v) => v >= 0 && v <= yMax);

  return (
    <div className="rounded-md border border-border bg-muted/10 p-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-auto"
        role="img"
        aria-label={`Cumulative score over ${rounds.length} rounds`}
      >
        {/* Grid lines + Y labels */}
        {gridYs.map((g) => (
          <g key={g}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(g)}
              y2={y(g)}
              stroke="currentColor"
              strokeWidth="0.5"
              className="text-muted-foreground/30"
              strokeDasharray={g === target ? "4 3" : undefined}
            />
            <text
              x={PAD.left - 4}
              y={y(g) + 3}
              textAnchor="end"
              className="fill-current text-muted-foreground text-[9px] font-display"
            >
              {g}
            </text>
          </g>
        ))}
        {/* X axis baseline */}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={y(0)}
          y2={y(0)}
          className="stroke-current text-border"
          strokeWidth="1"
        />
        {/* Round labels along X */}
        {rounds.map((r) =>
          r.round % Math.max(1, Math.ceil(lastRound / 8)) === 0 ||
          r.round === lastRound ? (
            <text
              key={r.round}
              x={x(r.round)}
              y={H - 6}
              textAnchor="middle"
              className="fill-current text-muted-foreground text-[9px] font-display"
            >
              {r.round}
            </text>
          ) : null,
        )}
        {/* Team 1 line + dots */}
        <polyline
          points={t1Points}
          fill="none"
          className="stroke-primary"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {rounds.map((r) => (
          <circle
            key={`t1-${r.round}`}
            cx={x(r.round)}
            cy={y(r.team1)}
            r="2.5"
            className="fill-primary"
          />
        ))}
        {/* Team 2 line + dots */}
        <polyline
          points={t2Points}
          fill="none"
          className="stroke-current text-muted-foreground"
          strokeWidth="2"
          strokeDasharray="5 3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {rounds.map((r) => (
          <circle
            key={`t2-${r.round}`}
            cx={x(r.round)}
            cy={y(r.team2)}
            r="2.5"
            className="fill-current text-muted-foreground"
          />
        ))}
      </svg>
      {/* Legend */}
      <div className="mt-2 flex items-center justify-between gap-4 text-[11px]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-primary rounded" />
          <span
            className={
              winningTeam === 1
                ? "font-semibold"
                : "text-muted-foreground"
            }
          >
            {team1Label}
          </span>
        </span>
        <span className="text-[10px] text-muted-foreground/80">
          target {target}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-muted-foreground rounded" />
          <span
            className={
              winningTeam === 2
                ? "font-semibold"
                : "text-muted-foreground"
            }
          >
            {team2Label}
          </span>
        </span>
      </div>
    </div>
  );
}

// Per-round signed bar chart. Bars above the 0-axis are team1 wins
// of magnitude netPoints; bars below are team2 wins. Reading the
// chart from left to right shows momentum / comebacks at a glance.
function RoundMomentumChart({
  rounds,
  team1Label,
  team2Label,
}: {
  rounds: CumRow[];
  team1Label: string;
  team2Label: string;
}) {
  const W = 600;
  const H = 140;
  const PAD = { top: 12, right: 8, bottom: 20, left: 28 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const nets = rounds.map((r) => r.net1);
  const absMax = Math.max(3, ...nets.map((n) => Math.abs(n)));
  // Bars are slim with a small gap; gap collapses on dense games.
  const gap = Math.max(1, Math.min(4, innerW / rounds.length / 5));
  const barW = Math.max(2, innerW / rounds.length - gap);
  const x = (i: number) =>
    PAD.left + (i + 0.5) * (innerW / rounds.length) - barW / 2;
  const yZero = PAD.top + innerH / 2;
  const yScale = (n: number) => (n / absMax) * (innerH / 2);

  return (
    <div className="rounded-md border border-border bg-muted/10 p-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-auto"
        role="img"
        aria-label="Net points per round"
      >
        {/* Center axis */}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={yZero}
          y2={yZero}
          className="stroke-current text-border"
          strokeWidth="1"
        />
        {/* Y axis ticks at ±absMax */}
        <text
          x={PAD.left - 4}
          y={PAD.top + 8}
          textAnchor="end"
          className="fill-current text-muted-foreground text-[9px] font-display"
        >
          +{absMax}
        </text>
        <text
          x={PAD.left - 4}
          y={H - PAD.bottom + 2}
          textAnchor="end"
          className="fill-current text-muted-foreground text-[9px] font-display"
        >
          −{absMax}
        </text>
        {rounds.map((r, i) => {
          const dy = yScale(r.net1);
          const y0 = dy >= 0 ? yZero - dy : yZero;
          const h = Math.abs(dy);
          if (r.net1 === 0) {
            // Tied round — small marker on the centerline so the
            // viewer can see it happened.
            return (
              <circle
                key={r.round}
                cx={x(i) + barW / 2}
                cy={yZero}
                r="2"
                className="fill-current text-muted-foreground"
              />
            );
          }
          return (
            <rect
              key={r.round}
              x={x(i)}
              y={y0}
              width={barW}
              height={Math.max(1, h)}
              className={r.net1 > 0 ? "fill-primary" : "fill-destructive"}
              opacity={0.85}
            />
          );
        })}
        {/* Round labels along bottom */}
        {rounds.map((r, i) =>
          r.round % Math.max(1, Math.ceil(rounds.length / 8)) === 0 ||
          i === rounds.length - 1 ? (
            <text
              key={r.round}
              x={x(i) + barW / 2}
              y={H - 6}
              textAnchor="middle"
              className="fill-current text-muted-foreground text-[9px] font-display"
            >
              {r.round}
            </text>
          ) : null,
        )}
      </svg>
      <div className="mt-2 flex items-center justify-between gap-4 text-[11px]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-2 bg-primary rounded-sm" />
          <span className="text-muted-foreground">{team1Label}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-2 bg-destructive rounded-sm" />
          <span className="text-muted-foreground">{team2Label}</span>
        </span>
      </div>
    </div>
  );
}

// Per-team bag accuracy. Computes totals across all frames that
// carry bag detail, then renders both a summary bar and a row of
// per-round 4-bag dot strips.
function BagAccuracyPanel({
  frames,
  team1Label,
  team2Label,
}: {
  frames: GameFrame[];
  team1Label: string;
  team2Label: string;
}) {
  // Per-team aggregates. Bags-per-round is 4 for each team in
  // standard cornhole. miss = 4 - inHole - onBoard.
  type Tally = {
    inHole: number;
    onBoard: number;
    miss: number;
    totalBags: number;
  };
  const blank = (): Tally => ({ inHole: 0, onBoard: 0, miss: 0, totalBags: 0 });
  const t1: Tally = blank();
  const t2: Tally = blank();
  for (const f of frames) {
    if (f.team1InHole != null || f.team1OnBoard != null) {
      const ih = f.team1InHole ?? 0;
      const ob = f.team1OnBoard ?? 0;
      t1.inHole += ih;
      t1.onBoard += ob;
      t1.miss += Math.max(0, 4 - ih - ob);
      t1.totalBags += 4;
    }
    if (f.team2InHole != null || f.team2OnBoard != null) {
      const ih = f.team2InHole ?? 0;
      const ob = f.team2OnBoard ?? 0;
      t2.inHole += ih;
      t2.onBoard += ob;
      t2.miss += Math.max(0, 4 - ih - ob);
      t2.totalBags += 4;
    }
  }

  return (
    <div className="space-y-3">
      <BagSummaryBar label={team1Label} t={t1} />
      <BagSummaryBar label={team2Label} t={t2} />
      <div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
          Bags by round (4 per team)
        </div>
        <div className="space-y-1.5">
          {frames.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 text-xs tabular-nums"
            >
              <span className="w-8 text-muted-foreground">R{f.roundNumber}</span>
              <BagDots
                inHole={f.team1InHole ?? 0}
                onBoard={f.team1OnBoard ?? 0}
                muted={f.team1InHole == null && f.team1OnBoard == null}
              />
              <span className="w-3 text-center text-muted-foreground">vs</span>
              <BagDots
                inHole={f.team2InHole ?? 0}
                onBoard={f.team2OnBoard ?? 0}
                muted={f.team2InHole == null && f.team2OnBoard == null}
              />
              <span className="flex-1 text-right text-muted-foreground">
                {f.scoringTeam === null
                  ? "tie"
                  : `+${f.netPoints} to T${f.scoringTeam}`}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BagSummaryBar({
  label,
  t,
}: {
  label: string;
  t: { inHole: number; onBoard: number; miss: number; totalBags: number };
}) {
  if (t.totalBags === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        {label}: no bag detail recorded
      </div>
    );
  }
  const ihPct = (t.inHole / t.totalBags) * 100;
  const obPct = (t.onBoard / t.totalBags) * 100;
  const missPct = (t.miss / t.totalBags) * 100;
  const grossPts = t.inHole * 3 + t.onBoard;
  const ppr = (grossPts / (t.totalBags / 4)).toFixed(1);
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {t.inHole} in / {t.onBoard} on / {t.miss} miss · gross {ppr} pts/round
        </span>
      </div>
      <div
        className="h-2.5 rounded-full overflow-hidden flex bg-muted/30"
        aria-label={`${ihPct.toFixed(0)}% in hole, ${obPct.toFixed(0)}% on board, ${missPct.toFixed(0)}% miss`}
      >
        <div className="bg-primary" style={{ width: `${ihPct}%` }} />
        <div className="bg-primary/40" style={{ width: `${obPct}%` }} />
        <div className="bg-destructive/60" style={{ width: `${missPct}%` }} />
      </div>
    </div>
  );
}

function BagDots({
  inHole,
  onBoard,
  muted,
}: {
  inHole: number;
  onBoard: number;
  muted: boolean;
}) {
  if (muted) {
    return <span className="text-muted-foreground/60 text-[10px]">—</span>;
  }
  const ih = Math.max(0, Math.min(4, inHole));
  const ob = Math.max(0, Math.min(4 - ih, onBoard));
  const miss = Math.max(0, 4 - ih - ob);
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${ih} in hole, ${ob} on board, ${miss} miss`}
    >
      {Array.from({ length: ih }).map((_, i) => (
        <span
          key={`ih-${i}`}
          className="inline-block w-2 h-2 rounded-full bg-primary"
        />
      ))}
      {Array.from({ length: ob }).map((_, i) => (
        <span
          key={`ob-${i}`}
          className="inline-block w-2 h-2 rounded-full bg-primary/40"
        />
      ))}
      {Array.from({ length: miss }).map((_, i) => (
        <span
          key={`m-${i}`}
          className="inline-block w-2 h-2 rounded-full bg-muted/50 border border-muted-foreground/30"
        />
      ))}
    </span>
  );
}

function FrameLogTable({
  frames,
  cum,
  team1Label,
  team2Label,
}: {
  frames: GameFrame[];
  cum: CumRow[];
  team1Label: string;
  team2Label: string;
}) {
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-xs tabular-nums">
        <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left p-2 font-display">R</th>
            <th className="text-left p-2 font-display">Outcome</th>
            <th className="text-right p-2 font-display">{team1Label}</th>
            <th className="text-right p-2 font-display">{team2Label}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {frames.map((f, i) => {
            const c = cum[i]!;
            return (
              <tr key={f.id}>
                <td className="p-2 text-muted-foreground">{f.roundNumber}</td>
                <td className="p-2">
                  {f.scoringTeam === null
                    ? "Tied"
                    : `+${f.netPoints} to T${f.scoringTeam}`}
                  {f.team1Gross != null && f.team2Gross != null && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {f.team1Gross}–{f.team2Gross}
                    </span>
                  )}
                </td>
                <td
                  className={`p-2 text-right ${
                    f.scoringTeam === 1 ? "font-semibold" : ""
                  }`}
                >
                  {c.team1}
                </td>
                <td
                  className={`p-2 text-right ${
                    f.scoringTeam === 2 ? "font-semibold" : ""
                  }`}
                >
                  {c.team2}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
